import React, { useCallback, useEffect, useRef, useState } from "react";
import { KeyRound, QrCode, Settings2, UserCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { UserPreferencesModal } from "./UserPreferencesModal";
import { LanguageSelector } from "./LanguageSelector";
import authApi from "../../utils/api";

const itemClass = "flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary";

export function UserMenu() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const skipFocusRestore = useRef(false);
  const openedByHover = useRef(false);
  const closeTimer = useRef(null);
  const drawerOpen = useRef(false);
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const handleDrawerOpenChange = useCallback((value) => {
    drawerOpen.current = value;
    window.clearTimeout(closeTimer.current);
  }, []);
  const scheduleClose = (event) => {
    if (event.pointerType !== "mouse" || drawerOpen.current) return;
    cancelClose();
    if (event.relatedTarget instanceof Element && event.relatedTarget.closest("[data-user-menu-surface]")) return;
    closeTimer.current = window.setTimeout(() => {
      if (drawerOpen.current) return;
      skipFocusRestore.current = true;
      setOpen(false);
      setQrOpen(false);
    }, 200);
  };
  useEffect(() => () => window.clearTimeout(closeTimer.current), []);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrError, setQrError] = useState("");
  useEffect(() => {
    return () => {
      if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
    };
  }, [qrImageUrl]);

  const loadQrCode = useCallback(async () => {
    setQrLoading(true);
    setQrError("");
    try {
      const response = await authApi._fetchWithRefresh(() =>
        fetch(authApi._url("/saas/login/get/qrcode"), {
          method: "GET",
          headers: authApi._headers("LOGIN_REQ", { Accept: "image/png" }),
        })
      );
      if (!response.ok) throw new Error(`Unable to load QR code (${response.status})`);

      const blob = await response.blob();
      if (blob.type && blob.type !== "image/png") throw new Error("The QR code response is not a PNG image.");
      setQrImageUrl(URL.createObjectURL(blob));
    } catch (error) {
      setQrError(error?.message || "Unable to load QR code.");
    } finally {
      setQrLoading(false);
    }
  }, []);

  const handleQrOpenChange = (open) => {
    setQrOpen(open);
    if (open && !qrImageUrl && !qrLoading) loadQrCode();
  };

  const closeForAction = () => {
    cancelClose();
    skipFocusRestore.current = true;
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={(nextOpen) => {
        cancelClose();
        openedByHover.current = false;
        setOpen(nextOpen);
        if (!nextOpen) handleQrOpenChange(false);
      }}>
        <PopoverTrigger asChild>
          <button data-user-menu-surface type="button" aria-label="Open user menu" title="User menu"
            onPointerLeave={scheduleClose}
            onPointerEnter={(event) => {
              cancelClose();
              if (event.pointerType === "mouse" && !open) {
                openedByHover.current = true;
                setOpen(true);
              }
            }}
            onClick={(event) => {
              if (open && openedByHover.current) {
                event.preventDefault();
                cancelClose();
                openedByHover.current = false;
              }
            }}
            className="grid h-8 w-8 place-items-center rounded-full border-0 bg-transparent text-white hover:bg-workspace/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white">
            <UserCircle2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent data-user-menu-surface align="end" className="w-52 p-1" aria-label="User menu"
          onPointerEnter={cancelClose}
          onPointerLeave={scheduleClose}
          onInteractOutside={(event) => {
            if (drawerOpen.current) event.preventDefault();
          }}
          onOpenAutoFocus={(event) => {
            if (openedByHover.current) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            if (skipFocusRestore.current) event.preventDefault();
            skipFocusRestore.current = false;
          }}>
          <div className="divide-y divide-border">
            <button type="button" className={itemClass} onClick={() => {
              closeForAction();
              setPreferencesOpen(true);
            }}>
              <Settings2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>My Settings</span>
            </button>
            <button type="button" className={itemClass} onClick={() => {
              closeForAction();
              navigate("/hrms-ess/change-password");
            }}>
              <KeyRound className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Change Password</span>
            </button>
            <LanguageSelector
              onPointerEnter={cancelClose}
              onPointerLeave={scheduleClose}
              onDrawerOpenChange={handleDrawerOpenChange}
            />
            <Popover open={qrOpen} onOpenChange={handleQrOpenChange}>
              <PopoverTrigger asChild>
                <button type="button" className={itemClass}>
                  <QrCode className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>Scan QR</span>
                </button>
              </PopoverTrigger>
            <PopoverContent data-user-menu-surface align="end" sideOffset={8} className="w-48 bg-white p-3 text-center" onPointerEnter={cancelClose} onPointerLeave={scheduleClose}>
              {qrLoading && <div className="py-12 text-xs text-slate-500">Loading QR code...</div>}
              {qrError && (
                <div className="py-4">
                  <p className="text-xs text-red-600">{qrError}</p>
                  <button type="button" onClick={loadQrCode} className="mt-3 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white">
                    Retry
                  </button>
                </div>
              )}
              {qrImageUrl && !qrLoading && !qrError && (
                <img src={qrImageUrl} alt="QR code" className="mx-auto h-40 w-40 object-contain" />
              )}
            </PopoverContent>
            </Popover>

          </div>
        </PopoverContent>
      </Popover>
      <UserPreferencesModal open={preferencesOpen} onOpenChange={setPreferencesOpen} />
    </>
  );
}
